import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Report an undone handoff (D247, CJ 2026-08-08).
//
// An opener who walks into an undone close is already behind on their own
// shift. Every second of friction here is a reason not to report, and
// unreported is the outcome this exists to fix — so: tap chips, optional
// photos, one comment box, send. No searching a checklist for the item that
// was missed, and no requirement that the item is on a checklist at all.
//
// Photos are downscaled in the browser before upload. A modern phone photo is
// 3–8 MB; on café wifi that is a slow upload and a mail attachment nobody can
// receive. 1600px on the long edge at q0.8 is plenty to see an oven left on.
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.8;
const MAX_PHOTOS = 10;

async function downscale(file) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
  const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  bitmap.close?.();
  return { base64: dataUrl, mime: 'image/jpeg', filename: (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg' };
}

// Yesterday in NY, which is what an opener is almost always reporting on.
function defaultShiftDate() {
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  now.setDate(now.getDate() - 1);
  return now.toISOString().slice(0, 10);
}

export function ShiftHandoffReportView({ currentUser, locations = [] }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [chips, setChips] = useState([]);
  const [picked, setPicked] = useState({});      // key -> { note, not_on_list, label }
  const [shiftKind, setShiftKind] = useState('close');
  const [shiftDate, setShiftDate] = useState(defaultShiftDate);
  const [locationId, setLocationId] = useState(
    currentUser.primary_location_id || currentUser.location_id || (locations[0] && locations[0].id) || ''
  );
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState('');
  const [mine, setMine] = useState([]);

  async function loadMine() {
    try {
      const r = await api('GET', '/api/me/shift-handoff', null, headers);
      setMine(Array.isArray(r?.reports) ? r.reports : []);
    } catch (_) { /* the form still works without the history */ }
  }
  useEffect(() => {
    api('GET', '/api/shift-handoff/items', null, headers)
      .then(r => setChips(r?.items || []))
      .catch(e => setErr(e?.message || String(e)));
    loadMine();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function toggle(key) {
    setPicked((cur) => {
      const next = { ...cur };
      if (next[key]) delete next[key];
      else next[key] = { note: '', not_on_list: false, label: '' };
      return next;
    });
  }

  async function addPhotos(fileList) {
    const files = Array.from(fileList || []).slice(0, MAX_PHOTOS - photos.length);
    if (!files.length) return;
    setErr('');
    try {
      const shrunk = await Promise.all(files.map(downscale));
      setPhotos((cur) => [...cur, ...shrunk]);
    } catch (e) {
      setErr('Could not read one of those photos. You can still send the report without it.');
    }
  }

  const chosen = Object.keys(picked);
  const canSend = chosen.length > 0 && !!locationId && !!shiftDate && !busy;

  async function submit() {
    if (!canSend) return;
    setBusy(true); setErr(''); setSent(null);
    try {
      const r = await api('POST', '/api/shift-handoff', {
        location_id: Number(locationId),
        shift_date: shiftDate,
        shift_kind: shiftKind,
        comment: comment.trim() || null,
        items: chosen.map((key) => ({
          key,
          label: picked[key].label || undefined,
          note: picked[key].note || undefined,
          not_on_list: !!picked[key].not_on_list,
        })),
        photos: photos.map((p) => ({ ...p, item_key: p.item_key || null })),
      }, headers);
      setSent(r);
      setPicked({}); setComment(''); setPhotos([]);
      await loadMine();
    } catch (e) {
      let m = e?.message || 'Could not send that.';
      try { m = JSON.parse(m).error || m; } catch (_) {}
      setErr(m);
    } finally { setBusy(false); }
  }

  const field = { border: '1px solid #d0d4d9', borderRadius: 8, padding: '8px 10px', fontSize: 14, fontFamily: 'inherit' };

  return (
    <div className="space-y-3" style={{ maxWidth: 620 }}>
      <div className="card">
        <div className="text-base font-semibold">Report a handoff issue</div>
        <div style={{ fontSize: 12, color: '#757575', marginTop: 2 }}>
          Walked into something the last shift didn’t finish? Tap what was missed, add a photo, send.
          It goes straight to your manager and CJ.
        </div>
      </div>

      {/* Receipt. Instant, and it names who it reached — the reporter needs to
          know this was not shouted into a void. */}
      {sent && (
        <div className="card" style={{ borderLeft: '4px solid var(--forest)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#2e5d42' }}>Sent. Thank you for flagging it.</div>
          <div style={{ fontSize: 12, color: '#37474f', marginTop: 3 }}>
            {sent.managers_emailed > 0
              ? <>Emailed to {sent.managers_emailed} manager{sent.managers_emailed === 1 ? '' : 's'}{sent.photos_stored ? ` with ${sent.photos_stored} photo${sent.photos_stored === 1 ? '' : 's'}` : ''}. You’ll see below when someone acknowledges it.</>
              : <>Your report was saved, but the email didn’t reach anyone. Please also tell your manager directly.</>}
          </div>
        </div>
      )}

      {err && <div className="card" style={{ borderLeft: '4px solid #c62828', fontSize: 13, color: '#a8323a' }}>{err}</div>}

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
          {/* CJ 2026-08-10: "there shouldn't be a need to select the location."
              It defaults to the café you're signed into, and only offers the
              picker if you can work more than one — someone who only ever works
              one store should not be asked a question with one answer. */}
          {locations.length > 1 ? (
            <label style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Which café
              <select value={locationId} onChange={(e) => setLocationId(e.target.value)} style={{ ...field, width: '100%', marginTop: 4 }}>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>
          ) : (
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Café
              <div style={{ ...field, marginTop: 4, background: '#f7f7f7', color: '#37474f' }}>
                {(locations.find((l) => String(l.id) === String(locationId)) || {}).name || '—'}
              </div>
            </div>
          )}
          <label style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Which shift
            <select value={shiftKind} onChange={(e) => setShiftKind(e.target.value)} style={{ ...field, width: '100%', marginTop: 4 }}>
              <option value="close">The close</option>
              <option value="open">The open</option>
              <option value="mid">Mid shift</option>
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Which day
            <input type="date" value={shiftDate} onChange={(e) => setShiftDate(e.target.value)}
              style={{ ...field, width: '100%', marginTop: 4 }} />
          </label>
        </div>

        <div>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 6 }}>What was missed?</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {chips.map((c) => {
              const on = !!picked[c.key];
              return (
                <button key={c.key} onClick={() => toggle(c.key)}
                  style={{ border: `1px solid ${on ? 'var(--forest)' : '#d0d4d9'}`, borderRadius: 999,
                           padding: '6px 13px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                           background: on ? 'var(--forest)' : '#fff', color: on ? '#fff' : '#37474f' }}>
                  {on ? '✓ ' : ''}{c.label}
                </button>
              );
            })}
          </div>
        </div>

        {chosen.map((key) => {
          const chip = chips.find((c) => c.key === key);
          return (
            <div key={key} style={{ border: '1px solid #eee', borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{chip?.label || key}</div>
              {key === 'other' && (
                <input value={picked[key].label} placeholder="What was it?"
                  onChange={(e) => setPicked((c) => ({ ...c, [key]: { ...c[key], label: e.target.value } }))}
                  style={{ ...field, width: '100%', marginBottom: 6 }} />
              )}
              <input value={picked[key].note} placeholder="Anything worth adding? (optional)"
                onChange={(e) => setPicked((c) => ({ ...c, [key]: { ...c[key], note: e.target.value } }))}
                style={{ ...field, width: '100%' }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#8a5a1f', marginTop: 6 }}>
                <input type="checkbox" checked={picked[key].not_on_list}
                  onChange={(e) => setPicked((c) => ({ ...c, [key]: { ...c[key], not_on_list: e.target.checked } }))} />
                This isn’t on our closing list
              </label>
            </div>
          );
        })}

        <div>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 6 }}>
            Photos {photos.length > 0 && <span style={{ color: '#9e9e9e' }}>· {photos.length}</span>}
          </div>
          <input type="file" accept="image/*" multiple capture="environment"
            onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }}
            style={{ fontSize: 12 }} disabled={photos.length >= MAX_PHOTOS} />
          {photos.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {photos.map((p, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={p.base64} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, border: '1px solid #e0e0e0' }} />
                  <button onClick={() => setPhotos((c) => c.filter((_, j) => j !== i))}
                    style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%',
                             border: '1px solid #d0d4d9', background: '#fff', cursor: 'pointer', fontSize: 11, lineHeight: 1 }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
          placeholder="Anything else your manager should know? (optional)"
          style={{ ...field, width: '100%', resize: 'vertical', boxSizing: 'border-box' }} />

        <button onClick={submit} disabled={!canSend}
          style={{ background: canSend ? 'var(--forest)' : '#e0e0e0', color: '#fff', border: 'none', borderRadius: 8,
                   padding: '10px 18px', fontSize: 14, fontWeight: 700, cursor: canSend ? 'pointer' : 'default', fontFamily: 'inherit' }}>
          {busy ? 'Sending…' : 'Send report'}
        </button>
      </div>

      {/* Their own reports, with acknowledgement and the outcome. This is the
          part that decides whether anyone reports a second time. */}
      {mine.length > 0 && (
        <div className="card">
          <div className="text-base font-semibold mb-1">Your reports</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {mine.map((r) => (
              <div key={r.id} style={{ border: '1px solid #eee', borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  {r.location_name} · {r.shift_date} {r.handoff_shift_kind}
                </div>
                <div style={{ fontSize: 12, color: '#37474f', marginTop: 2 }}>
                  {(r.items || []).map((i) => i.label).join(', ')}
                </div>
                <div style={{ fontSize: 12, marginTop: 5 }}>
                  {r.acknowledged_at
                    ? <span style={{ color: '#2e5d42', fontWeight: 600 }}>
                        ✓ Acknowledged{r.acknowledged_by_name ? ` by ${r.acknowledged_by_name}` : ''}
                      </span>
                    : <span style={{ color: '#8a5a1f', fontWeight: 600 }}>Waiting to be acknowledged</span>}
                </div>
                {r.reporter_note && (
                  <div style={{ fontSize: 12.5, color: '#37474f', background: '#f1f7f2', borderRadius: 6, padding: '6px 10px', marginTop: 6 }}>
                    <b>What happened:</b> {r.reporter_note}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
